import React from 'react';

const LegalMentions = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-blue-600 py-12 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold">Jobrise Extension</h1>
          <h2 className="text-xl mt-2">Privacy Policy</h2>
          <p className="text-sm opacity-80 mt-4">Last Updated: June 15, 2025</p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="bg-white shadow rounded-lg p-6 sm:p-8">
          <section className="mb-8">
            <h3 className="text-xl font-semibold text-blue-600 mb-4">Introduction</h3>
            <p className="mb-4 text-gray-700">
              Jobrise Extension is committed to protecting your privacy. 
              This Privacy Policy explains how our browser extension collects, uses, and shares information 
              about you when you use our Chrome extension for LinkedIn profile data extraction.
            </p>
            <p className="text-gray-700">
              By using the Jobrise Extension, you agree to the collection and use of information in 
              accordance with this policy.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-xl font-semibold text-blue-600 mb-4">Information We Collect</h3>
            
            <h4 className="text-lg font-medium text-gray-800 mt-4 mb-2">Information from LinkedIn Profiles</h4>
            <p className="mb-2 text-gray-700">
              When you explicitly activate our extension on a LinkedIn profile page by clicking the 
              extraction button, we collect professional information displayed on that profile, including:
            </p>
            <ul className="list-disc pl-5 mb-4 text-gray-700 space-y-1">
              <li>Name</li>
              <li>Professional title</li>
              <li>Current company</li>
              <li>Location/city</li>
              <li>LinkedIn profile URL</li>
              <li>Contact information (email and phone number, if publicly available)</li>
              <li>Profile photo URL</li>
            </ul>
            
            <h4 className="text-lg font-medium text-gray-800 mt-4 mb-2">Authentication Information</h4>
            <p className="text-gray-700">
              To connect with your Jobrise ATS account, we store an authentication token locally 
              on your device. This token allows us to verify your identity and permissions when 
              sending data to the Jobrise backend system.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-xl font-semibold text-blue-600 mb-4">How We Use Your Information</h3>
            <p className="mb-2 text-gray-700">We use the information we collect to:</p>
            <ul className="list-disc pl-5 text-gray-700 space-y-1">
              <li>Extract and send LinkedIn profile data to your Jobrise ATS system at your explicit request</li>
              <li>Verify if a profile has already been imported to prevent duplicates</li>
              <li>Authenticate your access to the Jobrise backend system</li>
              <li>Improve the functionality and user experience of our extension</li>
            </ul>
          </section>

          <section className="mb-8">
            <h3 className="text-xl font-semibold text-blue-600 mb-4">Information Sharing and Disclosure</h3>
            <p className="mb-2 text-gray-700">The information collected by our extension is shared only with:</p>
            <ul className="list-disc pl-5 mb-4 text-gray-700 space-y-1">
              <li>The Jobrise ATS backend system, where the profile data is stored in your account</li>
              <li>Your Jobrise ATS account that you have authenticated with</li>
            </ul>
            <p className="text-gray-700">
              We do not sell, rent, or otherwise share your information with any third parties outside of 
              the specific purpose of storing the extracted LinkedIn profile data in your Jobrise ATS account.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-xl font-semibold text-blue-600 mb-4">Data Security</h3>
            <p className="mb-2 text-gray-700">
              We implement appropriate security measures to protect against unauthorized access, alteration, 
              disclosure, or destruction of your information. These measures include:
            </p>
            <ul className="list-disc pl-5 mb-4 text-gray-700 space-y-1">
              <li>Storing authentication tokens securely using Chrome's storage API</li>
              <li>Using HTTPS for all data transmissions between the extension and our backend</li>
              <li>Implementing token-based authentication to verify user identity</li>
            </ul>
            <p className="text-gray-700">
              However, no method of electronic transmission or storage is 100% secure, and we cannot 
              guarantee absolute security.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-xl font-semibold text-blue-600 mb-4">Your Rights and Choices</h3>
            <p className="mb-2 text-gray-700">You have the following rights regarding your information:</p>
            <ul className="list-disc pl-5 text-gray-700 space-y-1">
              <li>You control when data extraction occurs by explicitly clicking the extraction button</li>
              <li>You can uninstall the extension at any time to prevent further data collection</li>
              <li>You can access, modify, or delete LinkedIn profile data stored in your Jobrise ATS account through the Jobrise web interface</li>
              <li>You can revoke the extension's access by changing your Jobrise account password</li>
            </ul>
          </section>

          <section className="mb-8">
            <h3 className="text-xl font-semibold text-blue-600 mb-4">Extension Permissions</h3>
            <p className="mb-2 text-gray-700">Our extension requires several permissions to function properly:</p>
            <ul className="list-disc pl-5 text-gray-700 space-y-1">
              <li><span className="font-medium">sidePanel:</span> To display the Jobrise interface in a side panel while browsing LinkedIn</li>
              <li><span className="font-medium">activeTab:</span> To access the current LinkedIn profile page content when you click the extract button</li>
              <li><span className="font-medium">storage:</span> To store your authentication token securely</li>
              <li><span className="font-medium">scripting:</span> To run the code necessary to extract profile data from LinkedIn pages</li>
              <li><span className="font-medium">tabs:</span> To detect when you navigate to LinkedIn profile pages</li>
              <li><span className="font-medium">webRequest:</span> To manage network communications with our backend</li>
              <li><span className="font-medium">webNavigation:</span> To detect when LinkedIn pages have fully loaded</li>
            </ul>
          </section>

          <section className="mb-8">
            <h3 className="text-xl font-semibold text-blue-600 mb-4">Changes to This Privacy Policy</h3>
            <p className="mb-4 text-gray-700">
              We may update our Privacy Policy from time to time. We will notify you of any changes by 
              posting the new Privacy Policy on this page and updating the "Last Updated" date.
            </p>
            <p className="text-gray-700">
              You are advised to review this Privacy Policy periodically for any changes. Changes to this 
              Privacy Policy are effective when they are posted on this page.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-semibold text-blue-600 mb-4">Contact Us</h3>
            <p className="mb-4 text-gray-700">
              If you have any questions or concerns about our Privacy Policy or data practices, please 
              contact us at:
            </p>
            <div className="bg-gray-50 p-4 rounded-md inline-block text-gray-700">
              <p>Email: florian@jobrise.tech</p>
              <p>Website: https://www.jobrise.tech/</p>
            </div>
          </section>
        </div>
      </main>

      <footer className="bg-blue-700 text-white py-6 mt-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p>&copy; guide-sourcing-linkedin-2024 Jobrise Extension. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default LegalMentions;